import React, { useState, useEffect, useRef } from 'react';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';

const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

const CustomDatePicker = ({ name, value, onChange, proposedMonth, minDate, compact = false, error = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
    const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
    const popoverRef = useRef(null);

    // Initialize calendar view month/year based on value or proposedMonth
    useEffect(() => {
        if (value) {
            const parts = value.split('-');
            if (parts.length === 3) {
                const y = parseInt(parts[0], 10);
                const m = parseInt(parts[1], 10);
                if (!isNaN(y) && !isNaN(m)) {
                    setCurrentMonth(m - 1);
                    setCurrentYear(y);
                }
            }
        } else if (proposedMonth) {
            const mIdx = MONTH_NAMES.indexOf(proposedMonth);
            if (mIdx !== -1) {
                setCurrentMonth(mIdx);
                // If proposed month has passed in current year, default to next year
                const now = new Date();
                const currentYr = now.getFullYear();
                if (mIdx < now.getMonth()) {
                    setCurrentYear(currentYr + 1);
                } else {
                    setCurrentYear(currentYr);
                }
            }
        }
    }, [value, proposedMonth, isOpen]);

    // Handle click outside to close
    useEffect(() => {
        const handleClick = (e) => {
            if (popoverRef.current && !popoverRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        if (isOpen) document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [isOpen]);

    // Helper to format YYYY-MM-DD to DD/MM/YYYY
    const formatDisplay = (val) => {
        if (!val) return compact ? 'Select Date' : 'dd/mm/yyyy';
        const parts = val.split('-');
        if (parts.length === 3) {
            return `${parts[2].padStart(2, '0')}/${parts[1].padStart(2, '0')}/${parts[0]}`;
        }
        return val;
    };

    // Calendar helper utilities
    const getDaysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
    const getFirstDayOfMonth = (y, m) => new Date(y, m, 1).getDay();

    const daysInMonth = getDaysInMonth(currentYear, currentMonth);
    const firstDay = getFirstDayOfMonth(currentYear, currentMonth);

    const prevMonth = () => {
        if (currentMonth === 0) {
            setCurrentMonth(11);
            setCurrentYear(prev => prev - 1);
        } else {
            setCurrentMonth(prev => prev - 1);
        }
    };

    const nextMonth = () => {
        if (currentMonth === 11) {
            setCurrentMonth(0);
            setCurrentYear(prev => prev + 1);
        } else {
            setCurrentMonth(prev => prev + 1);
        }
    };

    const handleDaySelect = (day) => {
        const selectedDate = `${currentYear}-${(currentMonth + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
        if (minDate && selectedDate < minDate) {
            return;
        }
        onChange({ target: { name, value: selectedDate } });
        setIsOpen(false);
    };

    // Generate day grid cells
    const dayCells = [];
    // Empty cells for alignment
    for (let i = 0; i < firstDay; i++) {
        dayCells.push(<div key={`empty-${i}`} style={{ width: '24px', height: '24px' }} />);
    }
    // Days of month
    for (let d = 1; d <= daysInMonth; d++) {
        const dateStr = `${currentYear}-${(currentMonth + 1).toString().padStart(2, '0')}-${d.toString().padStart(2, '0')}`;
        const isSelected = value === dateStr;
        const isToday = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }) === dateStr;
        const isDisabled = minDate && dateStr < minDate;

        dayCells.push(
            <button
                key={`day-${d}`}
                type="button"
                disabled={isDisabled}
                onClick={() => handleDaySelect(d)}
                style={{
                    width: '24px', height: '24px', borderRadius: '50%',
                    cursor: isDisabled ? 'not-allowed' : 'pointer', display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.72rem', fontWeight: isSelected || isToday ? 'bold' : 'normal',
                    background: isSelected ? '#3b82f6' : 'transparent',
                    color: isDisabled ? '#cbd5e1' : (isSelected ? 'white' : (isToday ? '#3b82f6' : '#334155')),
                    border: isToday && !isSelected ? '1px solid #3b82f6' : 'none',
                    transition: 'all 0.1s',
                    padding: 0
                }}
                onMouseEnter={e => { if (!isSelected && !isDisabled) e.target.style.background = '#f1f5f9'; }}
                onMouseLeave={e => { if (!isSelected && !isDisabled) e.target.style.background = 'transparent'; }}
            >
                {d}
            </button>
        );
    }

    return (
        <div ref={popoverRef} style={{ position: 'relative', width: '100%', zIndex: isOpen ? 9999 : 1 }}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                style={{
                    width: '100%',
                    padding: compact ? '3px 4px' : '0.65rem 0.75rem',
                    borderRadius: compact ? '4px' : '8px',
                    border: isOpen ? '1px solid #3b82f6' : (error ? '1px solid #ef4444' : '1px solid #cbd5e1'),
                    background: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    fontSize: compact ? '0.72rem' : '0.82rem',
                    fontWeight: value ? '600' : 'normal',
                    color: value ? '#0f172a' : '#94a3b8',
                    boxShadow: isOpen ? '0 0 0 2px rgba(59,130,246,0.1)' : 'none',
                    height: compact ? '24px' : '38px',
                    boxSizing: 'border-box'
                }}
            >
                <span>{formatDisplay(value)}</span>
                {!compact && <CalendarIcon size={14} color="#64748b" />}
            </button>

            {isOpen && (
                <div
                    style={{
                        position: 'absolute',
                        bottom: compact ? 'auto' : '100%',
                        top: compact ? '100%' : 'auto',
                        left: compact ? '-65px' : '0',
                        marginBottom: compact ? '0' : '0.5rem',
                        marginTop: compact ? '0.25rem' : '0',
                        background: 'white', borderRadius: '12px', padding: '0.75rem',
                        boxShadow: '0 10px 25px rgba(0,0,0,0.1), 0 4px 6px rgba(0,0,0,0.05)',
                        border: '1px solid rgba(0,0,0,0.05)', zIndex: 9999,
                        width: '215px', boxSizing: 'border-box'
                    }}
                >
                    {/* Calendar Header Controls */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                        <button type="button" onClick={prevMonth} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '2px' }}>
                            <ChevronLeft size={14} color="#64748b" />
                        </button>
                        <span style={{ fontSize: '0.76rem', fontWeight: 'bold', color: '#1e293b' }}>
                            {MONTH_NAMES[currentMonth]} {currentYear}
                        </span>
                        <button type="button" onClick={nextMonth} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '2px' }}>
                            <ChevronRight size={14} color="#64748b" />
                        </button>
                    </div>

                    {/* Weekday Titles */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', textAlign: 'center', fontWeight: 'bold', color: '#64748b', fontSize: '0.65rem', marginBottom: '4px', justifyItems: 'center' }}>
                        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((wd, idx) => (
                            <div key={`wd-${idx}`}>{wd}</div>
                        ))}
                    </div>

                    {/* Day Grid */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', justifyItems: 'center' }}>
                        {dayCells}
                    </div>
                </div>
            )}
        </div>
    );
};

export default CustomDatePicker;
